import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

/**
 * Thin wrapper over the shadcn Select for the app's two common shapes:
 * filter-bar dropdowns (`size="sm"`) and form fields (`size="default"`,
 * full-width). Design System v1 §8.4.
 *
 * Why a wrapper: nearly every native <select> we replace uses `value=""` for
 * an "All"/"Any"/placeholder option, but Radix Select THROWS on a
 * `SelectItem value=""`. SelectField maps the empty string to an internal
 * sentinel on the way in and back to '' on the way out, so call sites keep
 * their existing `value`/`onChange('')` semantics unchanged.
 */
export const SELECT_EMPTY_SENTINEL = '__select_empty__';

/** '' → sentinel (Radix forbids SelectItem value=""). */
export const toSelectValue = (v: string) => (v === '' ? SELECT_EMPTY_SENTINEL : v);
/** sentinel → '' (restore the caller's empty-string semantics). */
export const fromSelectValue = (v: string) => (v === SELECT_EMPTY_SENTINEL ? '' : v);

export type SelectFieldOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export function SelectField({
  value,
  onValueChange,
  options,
  placeholder,
  size = 'default',
  className,
  id,
  disabled,
  'aria-label': ariaLabel,
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectFieldOption[];
  placeholder?: string;
  size?: 'sm' | 'default';
  className?: string;
  id?: string;
  disabled?: boolean;
  'aria-label'?: string;
}) {
  return (
    <Select
      value={toSelectValue(value)}
      onValueChange={(v) => onValueChange(fromSelectValue(v))}
      disabled={disabled}
    >
      <SelectTrigger
        id={id}
        aria-label={ariaLabel}
        className={cn(
          size === 'sm' && 'h-8 w-auto min-w-[7rem] px-2.5 text-sm',
          className,
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem
            key={o.value}
            value={toSelectValue(o.value)}
            disabled={o.disabled}
          >
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
